import { useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { CheckCircle, Bot, Plus, RotateCcw, TriangleAlert, Sparkles, Pin, PinOff, Paperclip } from 'lucide-react';
import {
  AgentType,
  LlmChatSessionDto,
  LlmChatMessageDto,
  LlmChatSessionStatus,
  LlmChatMessageRole,
  stripEntityReferenceTokensToNames,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmButton } from '@/components/ConfirmButton';
import { LlmChatMessageList } from './LlmChatMessageList';
import { LlmChatInput, type ChatComposerDraft } from './LlmChatInput';
import { LlmChatAttachmentsSidebar } from './LlmChatAttachmentsSidebar';
import { LlmChatCompletionDialog } from './LlmChatCompletionDialog';
import { LlmChatModelSelect } from './LlmChatModelSelect';
import { useLlmChatStream } from '@/hooks/useLlmChatStream';
import { llmChatApi } from '@/lib/api/llm-chat';
import { llmChatSessionsQueryKey } from '@/lib/llm-chat-sessions-query';
import { inquiriesApi } from '@/lib/api/inquiries';
import { subscriptionsApi } from '@/lib/api/subscriptions';
import {
  buildInquiryCreateRoute,
  buildInquiryCreateState,
} from '@/lib/inquiry-create-navigation';
import { useLlmChatConceptScope } from '@/hooks/useLlmChatConceptScope';
import { userTurnHasAssistantTextReply } from '@/lib/llm-chat-reply';

function isWaitingForAssistantMessage(messages: LlmChatMessageDto[]): boolean {
  if (!messages.some((m) => m.role === LlmChatMessageRole.User)) return false;
  return !userTurnHasAssistantTextReply(messages);
}

/**
 * True when a persisted message is the echo of the optimistic one.
 *
 * Compares the reference tokens reduced to names: the server strips any `@` token the sender
 * cannot resolve, so the stored content need not match the sent content byte for byte.
 */
function echoesOptimistic(persisted: string | null | undefined, optimistic: string): boolean {
  return (
    stripEntityReferenceTokensToNames(persisted ?? '').trim() ===
    stripEntityReferenceTokensToNames(optimistic).trim()
  );
}

interface LlmChatWindowProps {
  session: LlmChatSessionDto;
  messages: LlmChatMessageDto[];
  initialMessage?: string | null;
  initialAttachmentIds?: string[];
  onInitialMessageConsumed?: () => void;
  onClearHistory?: () => Promise<void>;
  isClearingHistory?: boolean;
  composerOnly?: boolean;
  onBusyChange?: (busy: boolean) => void;
  afterMessages?: ReactNode;
  composerDraft?: ChatComposerDraft | null;
}

export function LlmChatWindow({
  session,
  messages,
  initialMessage,
  initialAttachmentIds,
  onInitialMessageConsumed,
  onClearHistory,
  isClearingHistory = false,
  composerOnly = false,
  onBusyChange,
  afterMessages,
  composerDraft,
}: LlmChatWindowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const scopeConceptId = useLlmChatConceptScope();
  const queryClient = useQueryClient();
  const { streamingContent, streamingUiAction, streamingTools, isStreaming, error, planLimitExceeded, streamEndedIncomplete, sendAndStream, resumeStream } =
    useLlmChatStream();
  const [showCompletion, setShowCompletion] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(null);
  const [optimisticAttachmentNames, setOptimisticAttachmentNames] = useState<string[]>([]);
  const sentInitialRef = useRef(false);

  const pinMutation = useMutation({
    mutationFn: (pinned: boolean) => llmChatApi.updateSessionPin(session.id, { pinned }),
    onSuccess: (updatedSession) => {
      queryClient.setQueryData(['llm-chat-session', session.id], (current: { session: LlmChatSessionDto; messages: LlmChatMessageDto[] } | undefined) =>
        current ? { ...current, session: updatedSession } : current,
      );
      void queryClient.invalidateQueries({ queryKey: llmChatSessionsQueryKey() });
    },
  });

  const { data: usage } = useQuery({
    queryKey: ['subscription-usage'],
    queryFn: () => subscriptionsApi.getUsage(),
    enabled: !session.isPreview,
    staleTime: 30_000,
  });
  const budgetRatio =
    usage?.budgetCents != null && usage.budgetCents > 0 ? usage.spentCents / usage.budgetCents : null;
  const budgetExhausted = planLimitExceeded || (budgetRatio !== null && budgetRatio >= 1);
  const showBudgetWarning = !budgetExhausted && budgetRatio !== null && budgetRatio >= 0.8;

  const isActive = session.status === LlmChatSessionStatus.Active;
  const isOffer = session.agentType === AgentType.OfferAdvisor;
  const isDecisionAgent = session.agentType === AgentType.DecisionAgent;
  const isChatOnly =
    session.agentType === AgentType.InquirerGuide ||
    session.agentType === AgentType.Generic ||
    isDecisionAgent;
  const blocksInquiryCreateFlow = !!session.inquiryId && !isOffer;
  const showInquiryCreateFlow = !isChatOnly && !blocksInquiryCreateFlow && !session.isPreview;

  const pendingUserMessage = useMemo(() => {
    if (!optimisticMessage) return null;
    const last = messages[messages.length - 1];
    if (last?.role === LlmChatMessageRole.User && echoesOptimistic(last.content, optimisticMessage)) {
      return null;
    }
    // Never show a raw [[ref:...]] token in the pending bubble.
    return stripEntityReferenceTokensToNames(optimisticMessage);
  }, [optimisticMessage, messages]);

  useEffect(() => {
    if (!optimisticMessage) return;
    const last = messages[messages.length - 1];
    if (last?.role === LlmChatMessageRole.User && echoesOptimistic(last.content, optimisticMessage)) {
      setOptimisticMessage(null);
      setOptimisticAttachmentNames([]);
    }
  }, [messages, optimisticMessage]);

  // A session can start with a fixed assistant message, so only a user message means the initial one was sent.
  const hasUserMessage = messages.some((m) => m.role === LlmChatMessageRole.User);

  useEffect(() => {
    const text = initialMessage?.trim() ?? '';
    const hasAttachments = (initialAttachmentIds?.length ?? 0) > 0;
    if ((!text && !hasAttachments) || sentInitialRef.current) return;
    if (!isActive || isStreaming) return;
    if (hasUserMessage) return;
    sentInitialRef.current = true;
    // Attachment-only submits need a placeholder message so the LLM has content to respond to.
    const content = text || (hasAttachments ? t('llmChat.attachmentsOnlyMessage') : '');
    queueMicrotask(() => setOptimisticMessage(content));
    void sendAndStream(session.id, content, initialAttachmentIds);
    // Clear deep-link router state only when present - a replace navigation changes
    // location.key and would otherwise auto-close the global chat drawer.
    if (location.state != null) {
      navigate({ pathname, search }, { replace: true, state: null });
    }
    onInitialMessageConsumed?.();
  }, [
    initialMessage,
    initialAttachmentIds,
    isActive,
    isStreaming,
    hasUserMessage,
    session.id,
    sendAndStream,
    navigate,
    pathname,
    search,
    location.state,
    onInitialMessageConsumed,
    t,
  ]);

  useEffect(() => {
    const awaitingInitial =
      !hasUserMessage &&
      Boolean(initialMessage?.trim() || (initialAttachmentIds?.length ?? 0) > 0);
    onBusyChange?.(isStreaming || Boolean(optimisticMessage) || awaitingInitial);
  }, [isStreaming, optimisticMessage, hasUserMessage, initialMessage, initialAttachmentIds, onBusyChange]);

  const chatAttachments = useMemo(() =>
    messages.flatMap((m) => m.attachments ?? []),
    [messages],
  );

  const showAssistantRetry =
    isActive && !isStreaming && (streamEndedIncomplete || isWaitingForAssistantMessage(messages));

  const handleSend = useCallback(async (content: string, files: File[]) => {
    const effectiveContent =
      content.trim() || (files.length > 0 ? t('llmChat.attachmentsOnlyMessage') : content);
    setOptimisticMessage(effectiveContent);
    setOptimisticAttachmentNames(files.map((file) => file.name));

    let attachmentIds: string[] = [];

    if (files.length > 0) {
      const uploads = await Promise.all(
        files.map((file) => llmChatApi.uploadAttachment(session.id, file)),
      );
      attachmentIds = uploads.map((a) => a.id);

      if (session.inquiryId) {
        try {
          await inquiriesApi.copyAttachmentsFromChatSession(session.inquiryId, session.id);
          void queryClient.invalidateQueries({ queryKey: ['inquiry', session.inquiryId] });
        } catch {
          // Attachment sync is best-effort; chat message still sends with uploaded files.
        }
      }
    }

    sendAndStream(session.id, effectiveContent, attachmentIds);
  }, [session.id, session.inquiryId, sendAndStream, queryClient, t]);

  return (
    <div className={composerOnly ? 'flex flex-col' : 'flex h-full min-h-0 flex-1 overflow-hidden'}>
      <div className={composerOnly ? 'flex flex-col' : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'}>
      {!composerOnly && (
      <div className="flex shrink-0 items-center justify-between border-b px-6 py-3">
        <div className="flex items-center gap-3">
          <Bot className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-sm font-semibold">
              {session.title || t('llmChat.untitledSession')}
            </h2>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-xs text-muted-foreground">
                {t(`llmChat.agentTypes.${session.agentType}.label`, { defaultValue: session.agentType })}
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <LlmChatModelSelect session={session} messageCount={messages.length} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!session.isPreview && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pinMutation.isPending}
              onClick={() => pinMutation.mutate(!session.pinned)}
              aria-pressed={Boolean(session.pinned)}
            >
              {session.pinned ? (
                <>
                  <PinOff className="mr-2 h-4 w-4" />
                  {t('llmChat.unpin')}
                </>
              ) : (
                <>
                  <Pin className="mr-2 h-4 w-4" />
                  {t('llmChat.pin')}
                </>
              )}
            </Button>
          )}
          {!isActive && (
            <Badge variant="secondary">{t('llmChat.completed')}</Badge>
          )}
          {showInquiryCreateFlow && !isActive && session.resultData && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isOffer && session.inquiryId) {
                  const state = {
                    chatDraft: session.resultData,
                    chatSessionId: session.id,
                    chatAttachments,
                  };
                  navigate(`/inquiries/${session.inquiryId}/offers/new`, { state });
                } else {
                  const state = buildInquiryCreateState({
                    chatDraft: session.resultData,
                    chatSessionId: session.id,
                    chatAttachments,
                  });
                  navigate(buildInquiryCreateRoute(scopeConceptId), { state });
                }
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t(isOffer ? 'llmChat.createNewOffer' : 'llmChat.createNewInquiry')}
            </Button>
          )}
          {isActive && messages.length > 0 && onClearHistory && !isDecisionAgent && (
            <ConfirmButton
              title={t('llmChat.clearHistory')}
              description={t('llmChat.clearHistoryConfirm')}
              confirmText="generic.confirm"
              cancelText="generic.cancel"
              variant="outline"
              size="sm"
              loading={isClearingHistory}
              disabled={isStreaming || isClearingHistory}
              onConfirm={onClearHistory}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('llmChat.clearHistory')}
            </ConfirmButton>
          )}
          {isActive && showInquiryCreateFlow && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompletion(true)}
              disabled={isStreaming || messages.length === 0}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {t('llmChat.completeAndCreate')}
            </Button>
          )}
          {!isDecisionAgent && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-pressed={attachmentsOpen}
              aria-label={attachmentsOpen ? t('llmChat.attachmentsHide') : t('llmChat.attachmentsShow')}
              onClick={() => setAttachmentsOpen((open) => !open)}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      )}

      {!composerOnly && (
      <LlmChatMessageList
        messages={messages}
        streamingContent={streamingContent}
        streamingUiAction={streamingUiAction}
        streamingTools={streamingTools}
        isStreaming={isStreaming}
        pendingUserMessage={pendingUserMessage}
        pendingAttachmentNames={pendingUserMessage ? optimisticAttachmentNames : undefined}
        showAssistantRetry={showAssistantRetry}
        onAssistantRetry={() => resumeStream(session.id)}
        onSend={(content) => void handleSend(content, [])}
        afterMessages={afterMessages}
      />
      )}

      {error && !budgetExhausted && (
        <div className="mx-6 mb-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {showBudgetWarning && (
        <div className="mx-6 mb-2 flex items-center gap-2 rounded-md border border-brand-primary/30 bg-brand-primary/10 px-4 py-2 text-sm text-foreground">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {t('llmChat.budget.warning', { percent: Math.min(Math.round(budgetRatio! * 100), 99) })}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings/subscription')}>
            {t('planLimits.upgradeCta')}
          </Button>
        </div>
      )}

      {budgetExhausted && (
        <div className="mx-6 mb-2 flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <Sparkles className="h-4 w-4 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">{t('llmChat.budget.exhaustedTitle')}</p>
            <p>{t('llmChat.budget.exhaustedDescription')}</p>
          </div>
          <Button size="sm" onClick={() => navigate('/settings/subscription')}>
            {t('planLimits.upgradeCta')}
          </Button>
        </div>
      )}

      {composerOnly && isActive && messages.length === 0 && (
        <div className="flex px-6 pb-1">
          <LlmChatModelSelect session={session} messageCount={messages.length} />
        </div>
      )}

      {isActive && (
        <LlmChatInput
          sessionId={session.id}
          onSend={handleSend}
          disabled={isStreaming || budgetExhausted}
          draft={composerDraft}
        />
      )}
      </div>

      {!composerOnly && !isDecisionAgent && attachmentsOpen && (
        <LlmChatAttachmentsSidebar
          sessionId={session.id}
          attachments={chatAttachments}
          onClose={() => setAttachmentsOpen(false)}
        />
      )}

      {showInquiryCreateFlow && (
        <LlmChatCompletionDialog
          sessionId={session.id}
          agentType={session.agentType}
          inquiryId={session.inquiryId}
          chatAttachments={chatAttachments}
          open={showCompletion}
          onOpenChange={setShowCompletion}
        />
      )}
    </div>
  );
}
